import React from 'react';
import PropTypes from 'prop-types';
import { FaBook, FaGlobeEurope, FaMusic, FaCalendarAlt } from 'react-icons/fa';
import { useTranslation } from '../../utils/translations';
import './DateGroupedView.css';

const iconForActivity = (id) => {
  switch (id) {
    case 'sundaySchool':
      return <FaBook aria-hidden="true" />;
    case 'russianSchool':
      return <FaGlobeEurope aria-hidden="true" />;
    case 'childrensChoir':
      return <FaMusic aria-hidden="true" />;
    default:
      return null;
  }
};

// Compact formatter that shows all teachers/leaders with proper wrapping
const joinWithLimit = (arr = [], limit = 2) => {
  if (!Array.isArray(arr) || arr.length === 0) return '';
  // Wrap each name in a span to prevent breaking individual names
  const wrappedNames = arr.map(name => `<span style="white-space: nowrap;">${name}</span>`);
  return wrappedNames.join(', ');
};

// Format birthdate for display
const formatBirthdate = (birthdate) => {
  if (!birthdate) return '';
  try {
    const [year, month, day] = birthdate.split('-').map(Number);
    const date = new Date(year, month - 1, day); // local date, not UTC
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  } catch (error) {
    return birthdate; // Return original if parsing fails
  }
};

const DateGroupedView = ({ students, activities }) => {
  const { t } = useTranslation();
  
  // Helper to get a student's activity details by id
  const getStudentActivity = (student, activityId) =>
    student.activities.find((sa) => sa.id === activityId);

  // Group all student activities by day
  const groupByDay = () => {
    const dayGroups = {};
    
    students.forEach(student => {
      ['sundaySchool', 'russianSchool', 'childrensChoir'].forEach(activityId => {
        const sa = getStudentActivity(student, activityId);
        if (sa?.details) {
          const day = sa.details.day;
          if (!dayGroups[day]) {
            dayGroups[day] = [];
          }
          
          const activity = activities.find(a => a.id === activityId);
          const d = sa.details;
          const room = d.classroom || d.choirRoom;
          const main = d.class || d.choirNumber;
          const people = d.teachers || d.choirLeaders;
          const labels = activityId === 'childrensChoir'
            ? { main: t('details.choirNumber'), people: t('details.choirLeaders') }
            : { main: t('details.class'), people: t('details.teachers') };

          dayGroups[day].push({
            studentName: student.name,
            studentBirthdate: student.birthdate,
            activityId,
            activityName: activity?.name || activityId,
            room,
            main,
            people,
            labels,
            details: d
          });
        }
      });
    });

    return dayGroups;
  };

  const dayGroups = groupByDay();
  
  // Define day order mapping for proper sorting (supports English and Russian, case-insensitive)
  const dayOrderMap = {
    // English
    'monday': 0, 'tuesday': 1, 'wednesday': 2, 'thursday': 3, 'friday': 4, 'saturday': 5, 'sunday': 6,
    // Russian
    'понедельник': 0, 'вторник': 1, 'среда': 2, 'четверг': 3, 'пятница': 4, 'суббота': 5, 'воскресенье': 6
  };
  
  const getDayOrder = (day) => {
    const normalizedDay = day.toLowerCase();
    return dayOrderMap[normalizedDay] !== undefined ? dayOrderMap[normalizedDay] : 999; // Unknown days go to end
  };
  
  const sortedDays = Object.keys(dayGroups).sort((a, b) => {
    return getDayOrder(a) - getDayOrder(b);
  });

  return (
    <div 
      className="date-grouped-layout" 
      role="main" 
      aria-label="Activities grouped by date"
      style={{ '--day-count': sortedDays.length }}
    >
      {sortedDays.map((day) => {
        const dayActivities = dayGroups[day];
        
        return (
          <div key={day} className="day-container">
            <div className="day-container-header">
              <div className="day-title-with-icon">
                <FaCalendarAlt className="day-icon" />
                <h3 className="day-title">{day}</h3>
              </div>
              <span className="day-count">
                {dayActivities.length} {dayActivities.length === 1 ? t('main.activity') : t('main.activities')}
              </span>
            </div>
            <div className="activities-on-day">
              {dayActivities.map((item, index) => (
                <div key={`${item.studentName}-${item.activityId}-${index}`} className="day-activity-card" data-activity={item.activityId}>
                  <div className="activity-student-info">
                    <div className="student-name-with-icon">
                      <h4 className="student-name">{item.studentName}</h4>
                    </div>
                    <span className="activity-name-badge">
                      <span className="activity-icon">{iconForActivity(item.activityId)}</span>
                      {t(`activities.${item.activityId}`) || item.activityName}
                    </span>
                  </div>
                  <div className="activity-details">
                    <div className="detail-row">
                      <span className="detail-label">{item.labels.main}:</span>
                      <span className="detail-value">{item.main}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t('details.room')}:</span>
                      <span className="detail-value">{item.room}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{item.labels.people}:</span>
                      <span 
                        className="detail-value" 
                        dangerouslySetInnerHTML={{ __html: joinWithLimit(item.people, 2) }}
                      ></span>
                    </div>
                    {item.studentBirthdate && (
                      <div className="detail-row">
                        <span className="detail-label">{t('details.birthday')}:</span>
                        <span className="detail-value">{formatBirthdate(item.studentBirthdate)}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

DateGroupedView.propTypes = {
  students: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      activities: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          status: PropTypes.string,
          details: PropTypes.object,
        })
      ).isRequired,
    })
  ).isRequired,
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      day: PropTypes.string,
    })
  ).isRequired,
};

export default DateGroupedView;
